import * as Pixi from "pixi.js";
import { LAYERS, LayerName } from "../../config/layers";
import { V, V2d } from "../Vector";
import { GameSprite } from "../entity/GameSprite";
import { Camera2d } from "./Camera2d";
import { LayerInfo } from "./LayerInfo";

/** Options for the GameRenderer2d constructor */
export interface GameRenderer2dOptions extends Partial<Pixi.RendererOptions> {}

/** The thing that renders stuff to the screen. Mostly for handling layers.
 * TODO: Document GameRenderer2d better
 */
export class GameRenderer2d {
  // TODO: Do we really need to store this ourselves? Can't we just set it on the canvas?
  private cursor: CSSStyleDeclaration["cursor"] = "auto";

  /** The number of sprites currently managed by this renderer. Mostly useful for debugging. */
  public get spriteCount() {
    return this._spriteCount;
  }
  private set spriteCount(value) {
    this._spriteCount = value;
  }
  private _spriteCount: number = 0;

  /** TODO: Document renderer.app */
  app: Pixi.Application;

  /** TODO: Document renderer.camera */
  camera: Camera2d;

  /** TODO: Document renderer.stage */
  get stage(): Pixi.Container {
    return this.app.stage;
  }

  /** TODO: Document renderer.canvas */
  get canvas(): HTMLCanvasElement {
    return this.app.renderer.canvas;
  }

  /** TODO: Document renderer constructor */
  constructor(
    private layerInfos: Record<LayerName, LayerInfo>,
    private defaultLayerName: LayerName,
    private onResize?: ([width, height]: [number, number]) => void,
  ) {
    this.app = new Pixi.Application();
    this.showCursor();
    this.camera = new Camera2d(this, V(0, 0));

    // "parent" is reserved by sprite placement (see SpritePlacement):
    // a layer with that name would be unreachable.
    if ("parent" in layerInfos) {
      throw new Error(`"parent" is a reserved placement, not a layer name`);
    }

    for (const layerInfo of Object.values(LAYERS)) {
      this.app.stage.addChild(layerInfo.container);
    }

    window.addEventListener("resize", this.handleResize);
  }

  async init(pixiOptions: GameRenderer2dOptions = {}) {
    await this.app
      .init({
        resizeTo: window,
        autoDensity: true,
        antialias: true,
        ...pixiOptions,
      })
      .then(() => {
        document.body.appendChild(this.canvas);
      });
  }

  /** TODO: Document request fullscreen */
  requestFullscreen() {
    const makeFullScreen = () => {
      this.canvas.requestFullscreen();
      this.canvas.removeEventListener("click", makeFullScreen);
    };
    this.canvas.addEventListener("click", makeFullScreen);
  }

  /**
   * Gets the effective height of the renderer viewport in logical pixels.
   * PIXI v8's renderer.width/height are already logical (the screen
   * rect, not the canvas's physical pixels), so no resolution division —
   * dividing again halved every screen measurement on hiDPI displays.
   */
  getHeight(): number {
    return this.app.renderer.height;
  }

  /**
   * Gets the effective width of the renderer viewport in logical pixels.
   */
  getWidth(): number {
    return this.app.renderer.width;
  }

  getSize(): V2d {
    return V(this.getWidth(), this.getHeight());
  }

  handleResize = () => {
    this.app.resizeTo = window;
    this.app.resize();
    this.onResize?.(this.getSize());
  };

  hideCursor() {
    this.cursor = "none";
  }

  showCursor() {
    this.cursor = "auto";
  }

  setCursor(value: CSSStyleDeclaration["cursor"]) {
    this.cursor = value;
  }

  // Render the current frame.
  render() {
    for (const layerInfo of Object.values(this.layerInfos)) {
      this.camera.updateLayer(layerInfo);
    }
    this.app.render();
    if (this.app.renderer.view.canvas.style) {
      this.app.renderer.view.canvas.style.cursor = this.cursor;
    }
  }

  /** Tear down the Pixi application and remove the canvas from the DOM. */
  destroy(): void {
    window.removeEventListener("resize", this.handleResize);
    this.canvas.remove();
    this.app.destroy(false, { children: true });
  }

  addSprite(sprite: GameSprite): GameSprite {
    const layerName = sprite.layerName ?? this.defaultLayerName;
    if (layerName === "parent") {
      // "parent" placement never reaches the renderer — Game.addEntity
      // mounts those into the ancestor's container instead.
      throw new Error(`addSprite got a "parent"-placed sprite`);
    }
    this.layerInfos[layerName].container.addChild(sprite);
    this.spriteCount += 1;
    return sprite;
  }

  // Take a sprite back out of the scene. Detaches from wherever it
  // actually sits — not the layer its name looks up — so a sprite that
  // was reparented after addSprite still comes out.
  removeSprite(sprite: GameSprite): void {
    sprite.parent?.removeChild(sprite);
    this.spriteCount -= 1;
  }

  /**
   * Adds a visual filter effect to a specific rendering layer.
   * @param filter - Pixi filter to apply to the layer
   * @param layerName - Name of the layer to apply the filter to
   */
  addLayerFilter(filter: Pixi.Filter, layerName: LayerName): void {
    const container = this.layerInfos[layerName].container;
    if (!(container.filters instanceof Array)) {
      throw new Error("layer.filters is not an array");
    }
    container.filters;
    container.filters = [...container.filters!, filter];
  }

  addStageFilter(filter: Pixi.Filter): void {
    if (!(this.stage.filters instanceof Array)) {
      throw new Error("stage.filters is not an array");
    }
    this.stage.filters = [...(this.stage.filters ?? []), filter];
  }

  removeStageFilter(filterToRemove: Pixi.Filter): void {
    if (!(this.stage.filters instanceof Array)) {
      throw new Error("stage.filters is not an array");
    }
    this.stage.filters = (this.stage.filters ?? []).filter(
      (filter) => filter != filterToRemove,
    );
  }
}
